import request from '@/utils/request';
import {
  captureUserSessionProfileRequest,
  noteUserSessionProfileLoaded,
} from '@/utils/user-session-profile';

/** 获取当前登录用户资料 GET /user/profile（OpenAPI: /liyuan-user/user/profile） */
export function getUserProfile() {
  const session = captureUserSessionProfileRequest();
  return request.get('/liyuan-user/user/profile').then((profile) => {
    noteUserSessionProfileLoaded(profile, session);
    // 资料就绪后补打 auth.register（首次失败不丢；经 Outbox 上报 51）
    if (profile) {
      queueMicrotask(() => {
        import('@/utils/analytics-auth-register')
          .then((m) => m.noteUserProfileLoaded(profile))
          .catch(() => {});
      });
    }
    return profile;
  });
}

/** 根据用户 id 获取用户资料 GET /user/getProfileByUserId/{userId} */
export function getProfileByUserId(userId) {
  const id = String(userId ?? '').trim();
  if (!id) return Promise.reject(new Error('userId required'));
  return request.get(`/liyuan-user/user/getProfileByUserId/${encodeURIComponent(id)}`);
}

/** 更新当前登录用户资料 PUT /liyuan-user/user/profile */
export function updateUserProfile(data = {}) {
  const body = {};
  const fields = [
    'avatar',
    'bio',
    'discord',
    'nickName',
    'phone',
    'twitter',
    'username',
    'websiteUrl',
  ];
  for (const key of fields) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      body[key] = data[key];
    }
  }
  return request.put('/liyuan-user/user/profile', body);
}

/** 分页查询当前用户市场浏览记录 GET /user/market-view/page */
export function getMarketViewPage(params = {}) {
  return request.get('/liyuan-user/market-view/page', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 10,
  });
}

/** 记录市场浏览 POST /user/market-view/record（存在则更新浏览时间；marketId 与评论 API 一致） */
export function recordMarketView(marketId) {
  const id = String(marketId ?? '').trim();
  if (!id) return Promise.reject(new Error('marketId required'));
  const numId = Number(id);
  return request.post('/liyuan-user/market-view/record', null, {
    params: { marketId: Number.isFinite(numId) ? numId : id },
  });
}

/** 添加市场收藏 POST /liyuan-user/market-favorite/add */
export function addMarketFavorite(collectionId) {
  const id = Number(collectionId);
  return request.post('/liyuan-user/market-favorite/add', null, {
    params: { collectionId: Number.isFinite(id) ? id : collectionId },
  });
}

/** 取消市场收藏 DELETE /liyuan-user/market-favorite/remove */
export function removeMarketFavorite(collectionId) {
  const id = Number(collectionId);
  return request.delete('/liyuan-user/market-favorite/remove', {
    collectionId: Number.isFinite(id) ? id : collectionId,
  });
}

/** 检查是否已收藏 GET /liyuan-user/market-favorite/check */
export function checkMarketFavorite(collectionId) {
  const id = Number(collectionId);
  return request.get('/liyuan-user/market-favorite/check', {
    collectionId: Number.isFinite(id) ? id : collectionId,
  });
}

/** 分页查询当前用户收藏列表 GET /liyuan-user/market-favorite/page（OpenAPI 无 query 参数） */
export function getMarketFavoritePage() {
  return request.get('/liyuan-user/market-favorite/page');
}

/** 推荐统计 GET /liyuan-user/referral/stats
 *  累计推荐卡片待后端补齐字段：totalReferralCount、activeReferralCount
 */
export function getReferralStats() {
  return request.get('/liyuan-user/referral/stats');
}

/** 最近 5 条推荐记录 GET /user/referral/recent */
export function getRecentReferrals() {
  return request.get('/liyuan-user/referral/recent');
}

/** 获取我的邀请码 GET /user/referral/code → string */
export function getReferralCode() {
  return request.get('/liyuan-user/referral/code');
}

/** 推荐佣金层级配置 GET /user/referral/config */
export function getReferralConfig() {
  return request.get('/liyuan-user/user/referral/config');
}

/** 查询当前用户赛季等级进度 GET /user/season/progress */
export function getSeasonProgress() {
  return request.get('/liyuan-user/season/progress');
}

/** 当前用户成就列表 GET /user/achievement/list */
export function getAchievementList() {
  return request.get('/liyuan-user/achievement/list');
}

/** 他人成就列表 GET /liyuan-user/achievement/other-list/{userId} */
export function getAchievementOtherList(userId) {
  const id = String(userId ?? '').trim();
  if (!id) return Promise.reject(new Error('userId required'));
  return request.get(`/liyuan-user/achievement/other-list/${encodeURIComponent(id)}`);
}
